import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import { formatToLocalTime } from '../utils/dateTimeHelper';

interface SuccessNotificationProps {
  open: boolean;
  message: string;
  title?: string;
  onClose: () => void;
}

/**
 * Format UTC timestamps in messages to user's local timezone
 * Detects patterns like "2026-02-06T10:12:00.000Z" and replaces with formatted local time
 */
const formatMessageWithLocalTime = (message: string): string => {
  // Regex to match ISO 8601 UTC timestamps
  const utcTimestampRegex = /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z/g;
  
  return message.replace(utcTimestampRegex, (utcTimestamp) => {
    return formatToLocalTime(utcTimestamp);
  });
};

const SuccessNotification: React.FC<SuccessNotificationProps> = ({
  open,
  message,
  title = 'Congratulations!',
  onClose
}) => {
  // Format any UTC timestamps in the message
  const formattedMessage = formatMessageWithLocalTime(message);
  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === 'backdropClick') return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          backgroundColor: '#fff'
        }
      }}
      BackdropProps={{
        sx: {
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      {/* Header with icon and close button - NO BORDER */}
      <DialogTitle 
        sx={{ 
          pb: 2,
          pt: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#fff'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CheckCircleIcon 
            sx={{ 
              color: '#4caf50',
              fontSize: 28 
            }} 
          />
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600, 
              color: '#000',
              fontSize: '20px',
              fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
            }}
          >
            {title}
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)'
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Message content */}
      <DialogContent sx={{ pb: 1, px: 3 }}>
        <Typography 
          variant="body1" 
          sx={{ 
            color: '#555',
            fontSize: 15,
            lineHeight: 1.6,
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif"
          }}
        >
          {formattedMessage}
        </Typography>
      </DialogContent>

      {/* Action buttons - NO BORDER, WHITE BACKGROUND */}
      <DialogActions 
        sx={{ 
          p: 2,
          pt: 3,
          gap: 1,
          backgroundColor: '#fff'
        }}
      >
        <Button
          variant="contained"
          onClick={onClose}
          sx={{
            backgroundColor: '#2196F3',
            color: '#fff',
            fontWeight: 600,
            px: 4,
            py: 1,
            textTransform: 'none',
            fontSize: 16,
            borderRadius: '8px',
            fontFamily: "'Roboto', 'Helvetica Neue', Arial, sans-serif",
            '&:hover': {
              backgroundColor: '#1976D2'
            }
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SuccessNotification;
